import React, { useEffect } from 'react';
import { ChevronLeft, ShieldCheck, FileText, Wallet, Check } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { v2, toMoney, INVOICE_TAX_RATE, getServiceName } from '../constants';

const YAPPY_CDN = 'https://bt-cdn.yappy.cloud/v1/cdn/web-component-btn-yappy.js';

export default function PaymentView() {
  // 仅在支付页加载 Yappy 脚本，避免全局 Firebase Auth 干扰
  useEffect(() => {
    if (document.querySelector(`script[src="${YAPPY_CDN}"]`)) return;
    const script = document.createElement('script');
    script.type = 'module';
    script.src = YAPPY_CDN;
    document.head.appendChild(script);
    return () => {
      // 离开支付页时移除脚本，防止下次进入重复注册
      const existing = document.querySelector(`script[src="${YAPPY_CDN}"]`);
      if (existing) existing.remove();
    };
  }, []);

  const {
    lang, t, userData,
    selectedService, quantity, orderInvoiceRequired, orderInvoiceDetails,
    appliedPromo, payMethod, setPayMethod, yappyPhone, setYappyPhone,
    handleFinalOrder, uploading, setView
  } = useAppContext();

  const selectedOrderBase = selectedService ? selectedService.price * quantity : 0;
  const selectedOrderDiscount = appliedPromo ? appliedPromo.discount : 0;
  const selectedOrderServiceSubtotal = toMoney(Math.max(0, selectedOrderBase - selectedOrderDiscount));
  const selectedOrderTaxAmount = orderInvoiceRequired ? toMoney(selectedOrderServiceSubtotal * INVOICE_TAX_RATE) : 0;
  const selectedOrderTotal = toMoney(selectedOrderServiceSubtotal + selectedOrderTaxAmount);
  const walletBalance = userData?.wallet_balance || 0;

  return (
    <div style={{ padding: '14px 16px 24px' }}>
      {/* 页头 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
        <button onClick={() => setView('order')} style={{ width: '38px', height: '38px', borderRadius: '8px', border: '1px solid #D8DEE8', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <ChevronLeft size={22} />
        </button>
        <div>
          <p style={{ margin: 0, color: v2.colors.muted, fontSize: '12px', fontWeight: '700' }}>{lang === 'es' ? 'Último paso' : '最后一步'}</p>
          <h2 style={{ fontWeight: '900', margin: 0, color: v2.colors.ink, fontSize: '21px' }}>{t.pay_select}</h2>
        </div>
      </div>

      {/* 订单摘要 */}
      <div style={{ ...v2.card, padding: '16px', marginBottom: '14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
          <div>
            <p style={{ margin: '0 0 5px 0', color: v2.colors.muted, fontSize: '12px', fontWeight: '700' }}>{lang === 'es' ? 'Pedido' : '订单'}</p>
            <b style={{ fontSize: '16px', color: v2.colors.ink }}>
              {getServiceName(selectedService?.service_name, lang)} {quantity > 1 ? `x ${quantity}` : ''}
            </b>
            {selectedOrderTaxAmount > 0 && (
              <p style={{ margin: '5px 0 0 0', fontSize: '11px', color: '#B45309', fontWeight: '800' }}>
                {lang === 'es'
                  ? `Servicio $${selectedOrderServiceSubtotal.toFixed(2)} + ITBMS $${selectedOrderTaxAmount.toFixed(2)}`
                  : `服务 $${selectedOrderServiceSubtotal.toFixed(2)} + ITBMS $${selectedOrderTaxAmount.toFixed(2)}`}
              </p>
            )}
          </div>
          <b style={{ fontSize: '24px', color: v2.colors.red }}>${selectedOrderTotal.toFixed(2)}</b>
        </div>

        {/* 保障提示 */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', background: v2.colors.softGold, border: '1px solid #FCD34D', padding: '10px', borderRadius: '8px' }}>
          <ShieldCheck size={18} color={v2.colors.gold} style={{ flexShrink: 0 }} />
          <p style={{ margin: 0, fontSize: '12px', color: '#854D0E', lineHeight: '1.45', fontWeight: '700' }}>
            {lang === 'es' ? 'Para activar la garantía de 30 días, paga dentro de FixBox.' : '通过 FixBox 完成支付，才能激活 30 天官方售后保障。'}
          </p>
        </div>

        {/* 发票说明 */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', background: orderInvoiceRequired ? '#FFF7E6' : '#F8FAFC', border: `1px solid ${orderInvoiceRequired ? '#FCD34D' : '#E3E8EF'}`, padding: '10px', borderRadius: '8px', marginTop: '10px' }}>
          <FileText size={18} color={orderInvoiceRequired ? '#B45309' : v2.colors.muted} style={{ flexShrink: 0, marginTop: '1px' }} />
          <p style={{ margin: 0, fontSize: '12px', color: orderInvoiceRequired ? '#854D0E' : v2.colors.muted, lineHeight: '1.45', fontWeight: '700' }}>
            {orderInvoiceRequired
              ? (lang === 'es'
                ? `Factura solicitada al técnico para ${orderInvoiceDetails.name || 'cliente'}. FixBox emitirá recibo de servicio.`
                : `已要求师傅开发票：${orderInvoiceDetails.name || '客户'}。FixBox 只出服务凭证。`)
              : (lang === 'es'
                ? 'No solicitaste factura fiscal; recibirás recibo de servicio FixBox.'
                : '未要求正式发票；你会收到 FixBox 服务凭证。')}
          </p>
        </div>
      </div>

      <p style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '900', color: v2.colors.ink }}>
        {lang === 'es' ? 'Selecciona cómo quieres pagar' : '请选择支付方式'}
      </p>

      {/* 余额支付 */}
      <div onClick={() => { if (walletBalance >= selectedOrderTotal) setPayMethod('wallet'); }}
        style={{ ...v2.card, padding: '16px', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', gap: '12px', cursor: walletBalance >= selectedOrderTotal ? 'pointer' : 'not-allowed', opacity: walletBalance >= selectedOrderTotal ? 1 : 0.55, border: payMethod === 'wallet' ? `2px solid ${v2.colors.green}` : v2.card.border, background: payMethod === 'wallet' ? v2.colors.softGreen : '#fff' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div style={{ width: '42px', height: '42px', borderRadius: '8px', background: '#ECFDF3', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Wallet size={21} color={v2.colors.green} />
          </div>
          <div>
            <b style={{ color: v2.colors.ink, fontSize: '15px' }}>{t.pay_wallet}</b>
            <p style={{ fontSize: '12px', margin: '5px 0 0 0', color: walletBalance >= selectedOrderTotal ? v2.colors.muted : v2.colors.red, fontWeight: '700' }}>
              {lang === 'es' ? 'Saldo' : '余额'}: ${walletBalance.toFixed(2)} {walletBalance < selectedOrderTotal && (lang === 'es' ? '(Insuficiente)' : '(余额不足)')}
            </p>
          </div>
        </div>
        {payMethod === 'wallet' && <Check color={v2.colors.green} />}
      </div>

      {/* Yappy 支付 */}
      <div onClick={() => setPayMethod('yappy')}
        style={{ ...v2.card, padding: '16px', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', cursor: 'pointer', alignItems: 'center', border: payMethod === 'yappy' ? '2px solid #2962FF' : v2.card.border, background: payMethod === 'yappy' ? '#F8FAFC' : '#fff' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div style={{ width: '42px', height: '42px', borderRadius: '8px', background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <img src="/yappy-logo.png" alt="Yappy" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
          </div>
          <div>
            <b style={{ color: v2.colors.ink, fontSize: '15px' }}>{t.pay_yappy}</b>
            <p style={{ fontSize: '12px', color: v2.colors.gold, margin: '5px 0 0 0', fontWeight: '800' }}>
              {lang === 'es' ? 'Confirmación automática' : '自动确认支付'}
            </p>
          </div>
        </div>
        {payMethod === 'yappy' && <Check color="#2962FF" />}
      </div>

      {/* 确认按钮 */}
      {payMethod === 'wallet' && (
        <button onClick={handleFinalOrder} disabled={uploading}
          style={{ width: '100%', padding: '17px', backgroundColor: v2.colors.green, color: '#fff', borderRadius: '8px', border: 'none', marginTop: '18px', fontWeight: '900', fontSize: '17px', cursor: 'pointer' }}>
          {t.pay_done}
        </button>
      )}

      {payMethod === 'yappy' && (
        <div style={{ marginTop: '18px', borderRadius: '16px', border: '2px solid #1B4F9B', background: 'linear-gradient(135deg, #EBF4FF 0%, #FFF7F0 100%)', padding: '20px 16px 18px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', width: '100%', boxSizing: 'border-box' }}>
          {/* Yappy 品牌标头 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', alignSelf: 'flex-start' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'linear-gradient(135deg, #3DBEF5, #1B4F9B)' }} />
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'linear-gradient(135deg, #F5A050, #F07B30)' }} />
            <span style={{ fontSize: '12px', fontWeight: '800', color: '#1B4F9B', letterSpacing: '0.5px' }}>
              {lang === 'es' ? 'Ingresa tu número Yappy' : '输入您的 Yappy 手机号'}
            </span>
          </div>
          {/* 输入框 */}
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', backgroundColor: '#fff', border: `2px solid ${yappyPhone.length >= 7 ? '#1B4F9B' : '#CBD5E1'}`, borderRadius: '12px', overflow: 'hidden', transition: 'border-color 0.2s', boxShadow: yappyPhone.length >= 7 ? '0 0 0 3px rgba(27,79,155,0.12)' : 'none' }}>
            <span style={{ padding: '0 12px', fontSize: '18px', color: '#1B4F9B', fontWeight: '900', borderRight: '2px solid #E5E7EB' }}>📱</span>
            <input
              type="tel"
              placeholder="6000-0000"
              maxLength="8"
              value={yappyPhone}
              onChange={e => setYappyPhone(e.target.value.replace(/\D/g, ''))}
              style={{ flex: 1, height: '52px', padding: '0 14px', border: 'none', outline: 'none', fontSize: '22px', fontWeight: '800', letterSpacing: '3px', color: '#1B4F9B', backgroundColor: 'transparent' }}
            />
            {yappyPhone.length >= 7 && (
              <span style={{ padding: '0 12px', fontSize: '18px' }}>✅</span>
            )}
          </div>
          {/* Yappy 支付按钮 */}
          <btn-yappy id="yappy-button" theme="darkBlue" rounded="true"></btn-yappy>
          <p style={{ margin: 0, fontSize: '11px', color: '#6B7280', textAlign: 'center' }}>
            {lang === 'es' ? '🔒 Pago procesado de forma segura por Banco General' : '🔒 由巴拿马 Banco General 银行安全处理'}
          </p>
        </div>
      )}

      {!payMethod && (
        <button disabled style={{ width: '100%', padding: '17px', backgroundColor: '#CBD5E1', color: '#fff', borderRadius: '8px', border: 'none', marginTop: '18px', fontWeight: '900', fontSize: '17px' }}>
          {t.pay_done}
        </button>
      )}
    </div>
  );
}
